import React, { useState, useEffect } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Button, Col, Row, Space, Tooltip, Upload } from 'antd';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from '@app/controllers/notificationController';
import { uploadExtensionCertifiedDocument } from '@app/api/export-extention.api';
import type { UploadProps } from 'antd';

interface ExtensionCertificate {
  export_extension_id: number;
  code: string;
  generated_certificate: string | null;
  certified_certificate: string | null;
  generated_certificate_url: string;
  certified_certificate_url: string;
}

interface ExtensionCertificateTableProps {
  exportExtensionId: number;
  certificate?: ExtensionCertificate | null;
  onGenerateCertificate: (exportExtensionId: number) => Promise<any>;
  onCertificateUpdate?: (certificate: ExtensionCertificate) => void;
  onRefresh?: () => Promise<void>;
}

const ExtensionCertificateTable: React.FC<ExtensionCertificateTableProps> = ({
  exportExtensionId,
  onGenerateCertificate,
  onCertificateUpdate,
  certificate,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const hasGenerated = !!certificate?.generated_certificate_url;
  const hasCertified = !!certificate?.certified_certificate_url;

  const handleGenerateCertificate = async () => {
    try {
      setLoading(true);

      await onGenerateCertificate(exportExtensionId);

      await onRefresh?.();

      notificationController.success({
        message: 'Extension certificate generated successfully.',
      });
    } catch (error: any) {
      notificationController.error({
        message: error?.message || 'Failed to generate certificate.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUploadCertifiedCertificate = async (file: File) => {
    try {
      setUploadLoading(true);

      const res = await uploadExtensionCertifiedDocument(exportExtensionId, file);

      const updatedCertificate = res?.data?.certificate;

      notificationController.success({
        message: res?.message || 'Certified certificate uploaded successfully.',
      });

      await onRefresh?.();

      // OPTION B (optimistic update fallback)
      if (updatedCertificate) {
        onCertificateUpdate?.(updatedCertificate);
      }

      return updatedCertificate;
    } catch (error: any) {
      notificationController.error({
        message: error?.message || 'Failed to upload certified certificate.',
      });

      return null;
    } finally {
      setUploadLoading(false);
    }
  };

  const uploadProps: UploadProps = {
    accept: '.pdf,.jpg,.jpeg,.png',
    showUploadList: false,
    maxCount: 1,

    beforeUpload: async (file) => {
      const updated = await handleUploadCertifiedCertificate(file);

      if (updated) {
        onCertificateUpdate?.(updated);
      }

      return false; // prevent default upload
    },
  };

  // const handleViewCertificate = (record: ExtensionCertificate) => {
  //   if (record.certificate_url) {
  //     window.open(record.certificate_url, '_blank');
  //   }
  // };

  useEffect(() => {
    console.log('certificate', certificate);
  }, [certificate]);

  const tableData = certificate
    ? [
        {
          key: 'generated',
          type: 'GENERATED',
          certificate_name: certificate.generated_certificate,
          certificate_code: certificate.code,
          file_url: certificate.generated_certificate_url,
          file_name: certificate.generated_certificate,
          hasFile: !!certificate.generated_certificate_url,
        },
        {
          key: 'certified',
          type: 'CERTIFIED',
          certificate_name: certificate.certified_certificate,
          certificate_code: certificate.code,
          file_url: certificate.certified_certificate_url,
          file_name: certificate.certified_certificate,
          hasFile: !!certificate.certified_certificate_url,
        },
      ]
    : [];

  const columns = [
    {
      title: 'Certificate Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (type === 'GENERATED' ? 'Generated Certificate' : 'Certified Certificate'),
    },
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      render: (value: string, record: any) => value || '-',
    },
    // {
    //   title: 'Certificate Code',
    //   dataIndex: 'certificate_code',
    //   key: 'certificate_code',
    // },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: any, record: any) => (
        <Space>
          {/* VIEW GENERATED */}
          {record.type === 'GENERATED' && record.file_url && (
            <Button icon={<EyeOutlined />} onClick={() => window.open(record.file_url, '_blank')}>
              View
            </Button>
          )}

          {/* VIEW CERTIFIED */}
          {record.type === 'CERTIFIED' && record.file_url && (
            <Button onClick={() => window.open(record.file_url, '_blank')}>View Certified</Button>
          )}

          {/* UPLOAD / REUPLOAD ONLY FOR CERTIFIED ROW */}
          {record.type === 'CERTIFIED' && certificate?.generated_certificate && (
            <Upload {...uploadProps}>
              <Button type="primary" loading={uploadLoading}>
                {certificate.certified_certificate_url ? 'Re-upload Certified Copy' : 'Upload Certified Copy'}
              </Button>
            </Upload>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24}>
        {certificate ? (
          <BaseTable rowKey="key" columns={columns} dataSource={tableData} pagination={false} />
        ) : (
          <div
            style={{
              minHeight: '200px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 12,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button type="primary" loading={loading} onClick={handleGenerateCertificate}>
                Generate Extension Certificate
              </Button>
            </div>
          </div>
        )}
      </Col>
    </Row>
  );
};

export default ExtensionCertificateTable;
